import { useCallback, useContext, useMemo } from "react";
import { Store } from "../store";

export interface Permission {
  model: string;
  permission: string;
}

/** Rôles Tracking (API) qui donnent accès à chaque permission UI. Utilisé en fallback quand app_permissions est vide (login Tracking). */
const TRACKING_DASHBOARD_ROLES = [
  "admin", "company", "driver", "parent", "student", "supervisor",
  "guard_agent", "guard_supervisor", "guard_manager", "guard_admin",
];
const TRACKING_PARENT_ROLES = ["parent", "admin", "company"];
const GUARD_ROLES = ["guard_agent", "guard_supervisor", "guard_manager", "guard_admin"];
const TRACKING_STUDENTS_ADMIN_ROLES = ["admin", "company"];

/** Rôles Admin (core) donnant accès au dashboard principal (Companies, etc.). */
const ADMIN_DASHBOARD_ROLES = ["admin", "company", "companyadmin", "superadmin"];
/** Rôles Tax donnant accès au tax dashboard. */
const TAX_DASHBOARD_ROLES = ["taxsuperadmin", "taxadmin", "taxcollector", "taxsupervisor", "dealer", "taxpayer", "debitor"];

const TRACKING_PERMISSION_ROLES: Record<string, string[]> = {
  "get dashboard": ADMIN_DASHBOARD_ROLES,
  "get taxdashboard": TAX_DASHBOARD_ROLES,
  "get tracking_dashboard": TRACKING_DASHBOARD_ROLES,
  "get tracking_profile": TRACKING_DASHBOARD_ROLES,
  "get tracking_notifications": TRACKING_DASHBOARD_ROLES,
  "get tracking_addresses": TRACKING_DASHBOARD_ROLES,
  "get tracking_trip_history": TRACKING_DASHBOARD_ROLES,
  "get tracking_parent": TRACKING_PARENT_ROLES,
  "get tracking_parent_payments": TRACKING_PARENT_ROLES,
  "get tracking_parent_invoices": TRACKING_PARENT_ROLES,
  "get tracking_students": TRACKING_STUDENTS_ADMIN_ROLES,
  "create tracking_student": TRACKING_STUDENTS_ADMIN_ROLES,
  "get guard_dashboard": GUARD_ROLES,
  "get guard_sites": GUARD_ROLES,
  "get guard_shifts": GUARD_ROLES,
  "get guard_incidents": GUARD_ROLES,
  "get guard_daily_report": GUARD_ROLES,
};

function getTrackingRoles(user: any): string[] {
  try {
    // user?.roles au niveau racine (réponse API Tracking)
    const fromArray = user?.roles;
    if (Array.isArray(fromArray) && fromArray.length) {
      return fromArray.map((r: any) => String(r).toLowerCase().trim()).filter(Boolean);
    }
    const fromString = user?.user?.roles;
    if (typeof fromString === "string" && fromString.trim()) {
      return fromString.split(/[\s,]+/).map((r: string) => r.toLowerCase().trim()).filter(Boolean);
    }
    // Certaines APIs renvoient un tableau roles vide mais un champ role (singulier)
    const fromRoleSingular = user?.user?.role;
    if (fromRoleSingular != null && fromRoleSingular !== "") {
      const r = String(fromRoleSingular).toLowerCase().trim();
      if (r) return [r];
    }
    // Fallback: role au niveau racine (réponse token)
    const rootRole = user?.role;
    if (rootRole != null && rootRole !== "") {
      const r = String(rootRole).toLowerCase().trim();
      if (r) return [r];
    }
    // Fallback: username "superadmin" quand l'API ne renvoie pas de rôles
    const username = (user?.user?.username ?? user?.username ?? "").toLowerCase();
    if (username === "superadmin") {
      return ["superadmin"];
    }
    return [];
  } catch {
    return [];
  }
}

export function usePermissions() {
  const {
    state: { user },
  } = useContext(Store);

  const trackingRoles = useMemo(
    () => getTrackingRoles(user),
    [user?.roles, user?.user?.roles, user?.user?.role, user?.role, user?.user?.username, user?.username]
  );

  // Version ultra-simple et stable des permissions
  // L'API Tracking retourne app_permissions à la racine, l'API Admin les met dans user
  const permissions: Permission[] = useMemo(() => {
    try {
      const rawPermissions =
        user?.user?.app_permissions ?? user?.app_permissions;
      if (!rawPermissions || !Array.isArray(rawPermissions)) {
        return [];
      }

      // Le backend retourne des objets avec {id, model, permission}
      return rawPermissions.map((p) => ({
        model: String(p?.model || ""),
        permission: String(p?.permission || ""),
      }));
    } catch (error) {
      console.warn("Erreur lors du chargement des permissions:", error);
      return [];
    }
  }, [
    user?.user?.app_permissions?.length,
    user?.user?.app_permissions?.map?.(p => `${p?.model}-${p?.permission}`)?.join?.("|"),
    user?.app_permissions?.length,
    user?.app_permissions?.map?.(p => `${p?.model}-${p?.permission}`)?.join?.("|"),
  ]);

  const has = useCallback(
    (permission: string) => {
      try {
        const requestedPermission = permission.toLowerCase().trim();
        if (!requestedPermission) return false;

        // 1) Vérification classique app_permissions
        if (permissions.length) {
          const fromApp = permissions.some((p: Permission) => {
            if (!p.model || !p.permission) return false;
            const fullPermission = `${p.permission} ${p.model}`.toLowerCase();
            const actionOnly = p.permission.toLowerCase();
            return fullPermission === requestedPermission || actionOnly === requestedPermission;
          });
          if (fromApp) return true;
        }

        // 2) Fallback Tracking : accès par rôles (réponse API Tracking)
        const allowedRoles = TRACKING_PERMISSION_ROLES[requestedPermission];
        if (allowedRoles && trackingRoles.length) {
          return trackingRoles.some((r) => allowedRoles.includes(r));
        }
        return false;
      } catch (error) {
        console.warn('Erreur lors de la vérification de permission:', error);
        return false;
      }
    },
    [permissions, trackingRoles]
  );

  const hasAny = useCallback(
    (permissionList: string[]) => {
      try {
        if (!permissionList?.length) return false;
        return permissionList.some(permission => has(permission));
      } catch (error) {
        console.warn('Erreur lors de la vérification hasAny:', error);
        return false;
      }
    },
    [has]
  );

  const hasAll = useCallback(
    (permissionList: string[]) => {
      try {
        if (!permissionList?.length) return false;
        return permissionList.every(permission => has(permission));
      } catch (error) {
        console.warn('Erreur lors de la vérification hasAll:', error);
        return false;
      }
    },
    [has]
  );

  const hasForModel = useCallback(
    (model: string, action: string) => {
      try {
        if (!permissions.length || !model || !action) return false;
        
        return permissions.some((p: Permission) => 
          p.model?.toLowerCase() === model.toLowerCase() && 
          p.permission?.toLowerCase() === action.toLowerCase()
        );
      } catch (error) {
        console.warn('Erreur lors de la vérification hasForModel:', error);
        return false;
      }
    },
    [permissions]
  );

  const getPermissionsForModel = useCallback(
    (model: string) => {
      try {
        if (!permissions.length || !model) return [];
        
        return permissions
          .filter((p: Permission) => p.model?.toLowerCase() === model.toLowerCase())
          .map((p: Permission) => p.permission);
      } catch (error) {
        console.warn('Erreur lors de getPermissionsForModel:', error);
        return [];
      }
    },
    [permissions]
  );

  return {
    permissions,
    has,
    hasAny,
    hasAll,
    hasForModel,
    getPermissionsForModel,
  };
}